import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'

import '@fontsource-variable/inter'
import './styles/tokens.css'
import './styles/base.css'
import './styles/components.css'
import './styles/layout.css'
// Below the desktop breakpoints, one file per area, after the rules they adjust (#111).
import './styles/responsive/touch.css'
import './styles/responsive/shell.css'
import './styles/responsive/lists.css'
import './styles/responsive/forms.css'
import './styles/responsive/document.css'
import './styles/responsive/dialogs.css'
import './styles/responsive/screens.css'

import i18n from './app/i18n.ts'
import { AuthProvider } from './app/auth.tsx'
import { clearCacheOnIdentityChange } from './app/sessionCache.ts'
import { ToastProvider } from './ui/Toast.tsx'
import { router } from './router.tsx'
import { applyTheme, readTheme } from './app/theme.ts'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A DeCA is a legal record, not a feed: refetching on every window focus
      // is noise. Writes invalidate what they touched, explicitly.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Retrying a 4xx just repeats the same refusal; only transport-level
        // failures are worth a second attempt.
        const status = (error as { status?: number }).status ?? 0
        if (status >= 400 && status < 500) return false
        return failureCount < 2
      },
    },
  },
})

// Emptied whenever the signed-in identity changes, so one user's cached list never shows to the
// next (Addendum No. 3, Point 7) — see `app/sessionCache.ts`.
clearCacheOnIdentityChange(queryClient)

document.documentElement.lang = i18n.language

// The saved theme, before anything renders. The top-bar toggle used to be the only thing that
// applied it, on mount, so /login and /restablecer (which have no top bar) opened in the system
// theme, and a phone layout that hides the toggle would have lost it everywhere (#111).
applyTheme(readTheme())
i18n.on('languageChanged', (language) => {
  document.documentElement.lang = language
})

const container = document.querySelector('#root')
if (!container) throw new Error('#root is missing from index.html')

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  </StrictMode>,
)
